import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarCheck, CalendarRange, MapPin, Plane, X } from "lucide-react";
import { SERVICIOS, RUTA_ZONAS, track } from "@/data/content";
import { useCertero } from "@/context/CerteroContext";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DIAS = ["L", "M", "X", "J", "V", "S", "D"];

export default function Calendario() {
  const { openLead, calServicio, setCalServicio } = useCertero();
  const hoy = new Date();
  const [mes, setMes] = useState(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
  const [servicioId, setServicioId] = useState("pulverizacion");
  // Rango de días: { ini, fin } — fin opcional (un solo día = rango de un día)
  const [rango, setRango] = useState({ ini: null, fin: null });
  const [vista, setVista] = useState("servicio");
  const servicio = SERVICIOS.find((s) => s.id === servicioId);

  // «Agendar servicio» desde una tarjeta: preselecciona el servicio y baja aquí
  useEffect(() => {
    if (calServicio) {
      setServicioId(calServicio);
      setVista("servicio");
      setCalServicio(null);
    }
  }, [calServicio, setCalServicio]);

  const anio = mes.getFullYear(), numMes = mes.getMonth();
  const primerIdx = (new Date(anio, numMes, 1).getDay() + 6) % 7; // lunes primero
  const totalDias = new Date(anio, numMes + 1, 0).getDate();
  const celdas = [...Array(primerIdx).fill(null), ...Array.from({ length: totalDias }, (_, i) => i + 1)];
  const esPasado = (d) => new Date(anio, numMes, d) < new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());

  const mover = (delta) => {
    setMes(new Date(anio, numMes + delta, 1));
    setRango({ ini: null, fin: null });
  };

  const elegirDia = (d) => {
    if (esPasado(d)) return;
    if (!rango.ini || rango.fin) setRango({ ini: d, fin: null });
    else if (d > rango.ini) setRango({ ini: rango.ini, fin: d });
    else setRango({ ini: d, fin: null });
  };

  const diaTxt = (d) => new Date(anio, numMes, d).toLocaleDateString("es-BO", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const fechaTxt = rango.ini
    ? rango.fin
      ? `Del ${diaTxt(rango.ini)} al ${rango.fin} de ${MESES[numMes].toLowerCase()} de ${anio}`
      : diaTxt(rango.ini)
    : null;

  const solicitar = () => {
    if (!rango.ini) return;
    track("calendario_solicitar", { servicio: servicio.t, fecha: fechaTxt });
    openLead("campana", `${servicio.t} — ${fechaTxt}`);
  };

  const enRango = (d) => {
    if (!rango.ini) return false;
    if (rango.fin) return d >= rango.ini && d <= rango.fin;
    return d === rango.ini;
  };

  return (
    <div id="servicios-calendario" data-testid="calendario-campanas" className="scroll-mt-24 border border-hud bg-ink">
      <div className="border-b border-hud p-7 sm:p-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-certero">Calendario de campañas y demos</p>
        <h3 className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-mist sm:text-2xl">
          Elija el servicio y las fechas tentativas
        </h3>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-smoke">
          Seleccione qué necesita y un rango de días aproximado: su solicitud llega directo al
          equipo y confirmamos disponibilidad y detalles por WhatsApp.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {[["servicio", "Por servicio"], ["zonas", "Ruta por zonas"]].map(([id, label]) => (
            <button
              key={id}
              data-testid={`calendario-vista-${id}`}
              onClick={() => setVista(id)}
              aria-pressed={vista === id}
              className={`rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                vista === id ? "bg-certero text-ink" : "border border-hud text-mist/70 hover:border-certero hover:text-certero"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {vista === "servicio" && (
        <div className="mt-5 flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Servicios del calendario">
          {SERVICIOS.map((s) => (
            <button
              key={s.id}
              data-testid={`calendario-tab-${s.id}`}
              onClick={() => setServicioId(s.id)}
              role="tab"
              aria-selected={servicioId === s.id}
              className={`shrink-0 rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                servicioId === s.id ? "bg-certero text-ink" : "border border-hud text-mist/70 hover:border-certero hover:text-certero"
              }`}
            >
              {s.t}
            </button>
          ))}
        </div>
        )}
      </div>

      {vista === "zonas" ? (
        <div className="grid gap-5 p-7 sm:p-8 lg:grid-cols-3" data-testid="calendario-zonas">
          <div className="border border-hud bg-abyss p-6">
            <CalendarRange className="h-5 w-5 text-certero" strokeWidth={1.5} />
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">Temporada</p>
            <p className="mt-1 font-display text-lg font-bold uppercase tracking-wide text-mist">{RUTA_ZONAS.campana}</p>
            <p className="mt-1 text-sm text-smoke">{RUTA_ZONAS.periodo}</p>
          </div>
          <div className="border border-hud bg-abyss p-6">
            <MapPin className="h-5 w-5 text-certero" strokeWidth={1.5} />
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">Zonas de operación</p>
            <ul className="mt-2 flex flex-col gap-1.5">
              {RUTA_ZONAS.zonas.map((z) => (
                <li key={z} className="text-sm leading-snug text-mist/85">{z}</li>
              ))}
            </ul>
          </div>
          <div className="border border-hud bg-abyss p-6">
            <Plane className="h-5 w-5 text-certero" strokeWidth={1.5} />
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">Actividades</p>
            <p className="mt-2 text-sm leading-relaxed text-mist/85">{RUTA_ZONAS.actividades}</p>
          </div>
          <p className="lg:col-span-3 font-mono text-[10px] uppercase tracking-[0.15em] text-smoke/70">
            Para reservar fechas en estas zonas, cambie a la vista «Por servicio», elija el servicio y el rango de días, y envíe su solicitud.
          </p>
        </div>
      ) : (
      <div className="grid gap-8 p-7 sm:p-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <div className="flex items-center justify-between">
            <button
              data-testid="calendario-prev"
              onClick={() => mover(-1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-hud text-mist/70 transition-colors duration-300 hover:border-certero hover:text-certero"
              aria-label="Mes anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <p className="font-display text-lg font-bold uppercase tracking-wide text-mist" data-testid="calendario-mes">
              {MESES[numMes]} {anio}
            </p>
            <button
              data-testid="calendario-next"
              onClick={() => mover(1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-hud text-mist/70 transition-colors duration-300 hover:border-certero hover:text-certero"
              aria-label="Mes siguiente"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-smoke/70">
            Haga clic en el día de inicio y luego en el día de fin para elegir un rango.
          </p>

          <div className="mt-3 grid grid-cols-7 gap-1.5" data-testid="calendario-grid">
            {DIAS.map((d) => (
              <span key={d} className="pb-2 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-smoke/60">{d}</span>
            ))}
            {celdas.map((d, i) =>
              d === null ? (
                <span key={`v${i}`} />
              ) : (
                <button
                  key={d}
                  data-testid={`calendario-dia-${d}`}
                  disabled={esPasado(d)}
                  onClick={() => elegirDia(d)}
                  aria-label={`${d} de ${MESES[numMes]}`}
                  className={`aspect-square border font-mono text-xs transition-colors duration-200 ${
                    d === rango.ini || d === rango.fin
                      ? "border-certero bg-certero font-bold text-ink"
                      : enRango(d)
                      ? "border-certero/60 bg-certero/15 text-mist"
                      : esPasado(d)
                      ? "border-transparent text-smoke/30"
                      : "border-hud text-mist/80 hover:border-certero hover:bg-certero/10"
                  }`}
                >
                  {d}
                </button>
              )
            )}
          </div>
        </div>

        <div className="flex flex-col justify-between border border-hud bg-abyss p-6 lg:col-span-5" data-testid="calendario-resumen">
          <div>
            <div className="flex items-start justify-between gap-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-certero">Su solicitud</p>
              {(rango.ini || rango.fin) && (
                <button
                  data-testid="calendario-btn-limpiar"
                  onClick={() => setRango({ ini: null, fin: null })}
                  className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] text-smoke transition-colors duration-300 hover:text-certero"
                >
                  <X className="h-3 w-3" /> Limpiar fechas
                </button>
              )}
            </div>
            <p className="mt-4 font-display text-base font-bold uppercase tracking-wide text-mist">{servicio.t}</p>
            <p className="mt-1 text-sm text-smoke">{servicio.d}</p>
            <div className="mt-5 border border-dashed border-hud p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">Fechas tentativas</p>
              <p className="mt-1 font-mono text-sm text-mist" data-testid="calendario-fecha-elegida">
                {fechaTxt ? fechaTxt : "Seleccione un día en el calendario →"}
              </p>
            </div>
          </div>
          <button
            data-testid="calendario-btn-solicitar"
            onClick={solicitar}
            disabled={!rango.ini}
            className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-certero font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,opacity] duration-300 hover:bg-[#4bc443] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <CalendarCheck className="h-4 w-4" />
            {rango.ini ? (rango.fin ? "Solicitar estas fechas" : "Solicitar esta fecha") : "Elija una fecha primero"}
          </button>
        </div>
      </div>
      )}
    </div>
  );
}
